import { THEME_LIGHT_MODE } from '@constants/features'
import { IMedia } from '@dtos/Course'
import { useHasFeature } from '@hooks/useThemeParams'
import { processHtmlContent } from '@utils/processHTMLContent'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { StyleSheet, Dimensions } from 'react-native'
import RenderHtml from 'react-native-render-html'
import YouTube from 'react-youtube'
import { Image, getToken, isWeb, useMedia, View } from 'tamagui'
import Vimeo from 'vimeo-reactjs'
import { ZoomableImage } from './ZoomableImage'
import WebView from 'react-native-webview'
import { VideoPlayer } from './VideoPlayer/VideoPlayer'
import { useMediaSize } from '@hooks/useMediaSize'
import { sparkVideosClient } from '@services/api/sparkvideos'
import { generateVideoQualityOptions } from '@utils/videoQualityOptions'
import { useMediaRenderWrapperDynamicHeight } from '@hooks/useMediaRenderWrapperDynamicHeight'
import { YoutubeVideoPlayer } from './YoutubeVideoPlayer'
import { VimeoVideoPlayer } from './VimeoVideoPlayer.tsx'
import { AudioPlayer } from './AudioPlayer'

interface IMediaRenderProps {
  media: IMedia
  children?: React.ReactNode
  thumbnailUrl?: string
  isImageVariant?: boolean
  isExpandedViewPage?: boolean
  location?: 'extraContent' | 'courseContent'
}

export interface ISparkVideosUrls {
  mainUrl: string
  qualities: {
    [key: number]: string
  }
  thumbnailUrl: string
  isVertical: boolean
  width: number
  height: number
}

export function MediaRender({
  media,
  children,
  isImageVariant = false,
  isExpandedViewPage = false,
  thumbnailUrl,
  location,
}: IMediaRenderProps) {
  const [isLoadingVideo, setIsLoadingVideo] = useState(false)
  const [sparkVideosUrls, setSparkVideosUrls] =
    useState<ISparkVideosUrls | null>(null)
  const isLightMode = useHasFeature(THEME_LIGHT_MODE) ?? false
  const Tmedia = useMedia()
  const horizontalPadding = media.type === 'Image' ? 0 : 40
  const windowWidth = Dimensions.get('window').width
  const windowHeight = Dimensions.get('window').height
  const canShowVideo = !isLoadingVideo && sparkVideosUrls !== null
  const mediaSize = useMediaSize()
  const mediaRenderWrapperHeight = useMediaRenderWrapperDynamicHeight({
    mediaSize,
    mediaType: media.type,
    isExpanded: isExpandedViewPage,
    textHeight: 0,
    location,
    fileExtension: media.fileExt,
  })
  const videoBorderRadius = getToken('$border-radius-md')

  const { width, height } = useMemo(() => {
    const width = Tmedia.gtSm
      ? Tmedia.gtMd
        ? 958
        : 576
      : windowWidth - horizontalPadding
    const height = () => {
      if (Tmedia.gtSm) {
        return windowHeight * 0.6
      } else if (media?.type === 'Video') {
        return mediaRenderWrapperHeight
      } else if (media?.type === 'Audio') {
        return 184
      } else {
        return 450
      }
    }
    return { width, height }
  }, [
    Tmedia.gtSm,
    Tmedia.gtMd,
    windowWidth,
    horizontalPadding,
    media?.type,
    windowHeight,
    mediaRenderWrapperHeight,
  ])

  const isMediaPdf = media?.fileExt?.toLowerCase() === 'pdf'

  const opts = {
    height: height.toString(),
    width: width.toString(),
    playerVars: {
      controls: 1,
    },
  }

  const sparkVideosUrl = (media: IMedia) => {
    const mountSparkVideosUrl = `${process.env.EXPO_PUBLIC_SPARKVIDEOS_URL}/api/player/${media.organizationUuid}/${media.hostKey}`
    return mountSparkVideosUrl
  }

  const videoPlaylist = useCallback(async () => {
    if (
      sparkVideosUrls !== null ||
      !media.organizationUuid ||
      media.host !== 'SparkVideos'
    )
      return
    const getsparkVideosUrl = async () => {
      try {
        setIsLoadingVideo(true)
        const response = await sparkVideosClient.get(
          `/api/player/${media.organizationUuid}/${media.hostKey}/urls`,
        )
        setSparkVideosUrls({
          mainUrl: response.data.main_url,
          qualities: response.data.qualities,
          thumbnailUrl: response.data.thumbnail,
          isVertical: response.data.width < response.data.height,
          width: response.data.width,
          height: response.data.height,
        })
      } catch (error) {
        console.log('error', error)
      } finally {
        setIsLoadingVideo(false)
      }
    }

    getsparkVideosUrl()
  }, [sparkVideosUrls, media.organizationUuid, media.host, media.hostKey])

  const qualityOptions = useMemo(() => {
    return generateVideoQualityOptions(sparkVideosUrls)
  }, [sparkVideosUrls])

  useEffect(() => {
    videoPlaylist()
  }, [videoPlaylist])

  const styles = useMemo(() => {
    return StyleSheet.create({
      container: {
        flex: 1,
        width,
        height: height(),
      },
      video: {
        alignSelf: 'center',
        width: '100%',
        height: height(),
        borderRadius: videoBorderRadius,
        overflow: 'hidden',
      },
    })
  }, [width, height, videoBorderRadius])

  const youtubeVideoId = useMemo(() => {
    return media?.fileUrl?.split('v=')[1]
  }, [media])

  const vimeoVideoId = useMemo(() => {
    return media?.fileUrl?.split('vimeo.com/')[1]
  }, [media])

  if (media === null) return null
  if (media.type === 'WebinarHerospark' && media.originUrl) {
    return (
      <View style={styles.video}>
        <WebView
          source={{ uri: media.originUrl }}
          style={{
            borderRadius: videoBorderRadius,
            backgroundColor: 'transparent',
          }}
        />
      </View>
    )
  }
  if (media.type === 'Document' && media.fileUrl) {
    if (isMediaPdf) {
      return (
        <View style={styles.container}>
          <WebView
            originWhitelist={['*']}
            source={{
              uri:
                'https://static-cdn.myedools.com/pdfjs/web/viewer.html?file=' +
                encodeURIComponent(media.fileUrl),
            }}
            style={{ width, height: height() < 700 ? 450 : height() }}
          />
        </View>
      )
    }
  }

  if (media.type === 'Audio' && media.fileUrl) {
    if (isWeb) {
      return (
        <View style={(styles.container, { justifyContent: 'center' })}>
          <audio controls style={{ width }}>
            <source src={media.fileUrl} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </View>
      )
    } else {
      return (
        <View
          overflow="hidden"
          borderRadius="$border-radius-md"
          height={height()}
          backgroundColor="$neutral-200"
        >
          <AudioPlayer url={media.fileUrl} />
        </View>
      )
    }
  }

  if (media.type === 'Text' && media.text) {
    return (
      <RenderHtml
        contentWidth={width}
        source={{ html: processHtmlContent(media.text) }}
        baseStyle={{
          color: isLightMode ? '#262626' : '#F2F2F2',
          paddingBottom: 8,
        }}
        enableExperimentalBRCollapsing
        enableExperimentalGhostLinesPrevention
        defaultTextProps={{
          selectable: true,
        }}
      />
    )
  }

  if (media.type === 'Image' && media.fileUrl) {
    if (isImageVariant) {
      return (
        <>
          {isExpandedViewPage ? (
            <ZoomableImage
              uri={media.fileUrl}
              width={windowWidth - windowWidth * 0.01}
              height={windowHeight - windowHeight * 0.1}
              objectFit="contain"
            />
          ) : (
            <Image
              alt={media.title}
              source={{
                uri: media.fileUrl,
              }}
              style={{
                width,
                height: height(),
              }}
              resizeMode="cover"
            />
          )}
        </>
      )
    }

    return (
      <View>
        <Image
          alt={media.title}
          source={{
            uri: media.fileUrl,
          }}
          style={{ height: height(), width }}
        />
      </View>
    )
  }

  if (media && media.type === 'Video' && media.fileUrl) {
    if (isWeb) {
      if (media.fileUrl.includes('youtube')) {
        return (
          <View
            style={(styles.video, { alignSelf: 'center', height: height() })}
          >
            <YouTube
              videoId={youtubeVideoId}
              id="video"
              opts={opts}
              style={{ display: 'flex', flex: 1 }}
            />
          </View>
        )
      } else if (media.fileUrl.includes('vimeo')) {
        return (
          <View style={(styles.container, { alignSelf: 'center' })}>
            <Vimeo video={vimeoVideoId} height={height()} width={width} />
          </View>
        )
      } else {
        return (
          <View style={styles.container}>
            <iframe
              src={sparkVideosUrl(media)}
              style={{ width, height: height() }}
            ></iframe>
          </View>
        )
      }
    } else {
      if (media.host === 'Youtube') {
        return (
          <View style={styles.video}>
            <YoutubeVideoPlayer
              height={mediaRenderWrapperHeight}
              videoId={youtubeVideoId}
              webViewStyle={{
                borderRadius: videoBorderRadius,
              }}
            />
          </View>
        )
      } else if (media.host === 'Vimeo') {
        return (
          <View style={styles.video}>
            <VimeoVideoPlayer videoId={vimeoVideoId || ''} />
          </View>
        )
      } else {
        return (
          canShowVideo && (
            <VideoPlayer
              videoUrl={sparkVideosUrls}
              qualityOptions={qualityOptions}
              title={media.title}
              videoContainerHeight={height()}
              thumbnailUrl={thumbnailUrl || sparkVideosUrls?.thumbnailUrl}
              mediaUrl={media.fileUrl}
              isVertical={sparkVideosUrls?.isVertical}
            />
          )
        )
      }
    }
  }

  return (
    <View style={(styles.container, { justifyContent: 'center' })}>
      {children}
    </View>
  )
}
